import React, { useRef, useState, useEffect } from 'react'
import Button from '/react4xp/components/Button'
import RichText from '/react4xp/components/RichText'

interface Props {
   tag?: string,
   title?: string,
   text?: string,
   img?: {
      src?: string,
      srcMobile?: string,
      alt?: string,
   },
   video?: {
      src?: string,
      options?: {
         autoplay?: boolean,
         loop?: boolean,
         muted?: boolean,
         playsInline?: boolean,
      },
   }
   twoColumnsContent?: {
      title?: string,
      firstColumn?: string,
      secondColumn?: string,
      cta?: {
         label?: string,
         href?: string,
         isExternal?: boolean
      },
   },
}

const isContentEmpty = (content?: string) => !content || content.trim() === ''

const shouldRenderTwoColumns = (twoColumnsContent?: Props['twoColumnsContent']) => {
   if (!twoColumnsContent) return false

   const { title, firstColumn, secondColumn, cta } = twoColumnsContent
   const isCtaEmpty = !cta || isContentEmpty(cta.label) || isContentEmpty(cta.href)

   return !(isContentEmpty(title) && isContentEmpty(firstColumn) && isContentEmpty(secondColumn) && isCtaEmpty)
}

export default function SolutionsPageHero(props: Props) {
   const { tag, title, text, img, video, twoColumnsContent } = props

   const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 992)

   const calculateMobile = () => {
      setIsMobile(window.innerWidth < 992)
   }

   const handleResize = () => {
      calculateMobile()
   }

   useEffect(() => {
      window.addEventListener('resize', handleResize)

      return () => {
         window.removeEventListener('resize', handleResize)
      }
   }, [])

   return (
      <div className="solutions-page-hero">
         <div className="solutions-page-hero__wrapper grid-container">

            <div className="grid-row">
               <div className="c-md-8 start-md-3">
                  <div className="solutions-page-hero__content">
                     {tag && <div className="solutions-page-hero__tag">{tag}</div>}
                     {title && <h1 className="solutions-page-hero__title">{title}</h1>}
                     {text && <div className="solutions-page-hero__text"><RichText content={text} /></div>}
                  </div>
               </div>
            </div>

            {img &&
               <div className="grid-row">
                  <div className="c-12">
                     <div className="solutions-page-hero__image">
                        <picture>
                           <source
                              media="(min-width: 768px)"
                              srcSet={img.src}
                           />
                           <source
                              media="(max-width: 767px)"
                              srcSet={img.srcMobile}
                           />
                           <img src={img.src} alt={img.alt ? img.alt : 'Image'} />
                        </picture>
                     </div>
                  </div>
               </div>
            }

            {video &&
               <div className="grid-row">
                  <div className="c-12">
                     <div className="solutions-page-hero__video">
                        <video src={video.src} {...video.options} />
                     </div>
                  </div>
               </div>
            }
         </div>

         {shouldRenderTwoColumns(twoColumnsContent) &&
            <div className="solutions-two-column">
               <div className="grid-container">
                  <div className="grid-row solutions-two-column__wrapper">
                     {twoColumnsContent?.title &&
                        <div className="c-md-4">
                           <h2 className="solutions-two-column__title">{twoColumnsContent.title}</h2>
                        </div>
                     }
                     {twoColumnsContent?.firstColumn &&
                        <div className="c-md-4">
                           <div className="solutions-two-column__content"><RichText content={twoColumnsContent.firstColumn} /></div>
                        </div>
                     }
                     {twoColumnsContent?.secondColumn &&
                        <div className="c-md-4">
                           <div className="solutions-two-column__content"><RichText content={twoColumnsContent.secondColumn} /></div>
                        </div>
                     }
                  </div>
                  {(twoColumnsContent?.cta && !isContentEmpty(twoColumnsContent.cta.href)) &&
                     <div className="grid-row">
                        <div className="c-12">
                           <div className="solutions-two-column__cta">
                              <Button href={twoColumnsContent.cta.href} target={twoColumnsContent.cta.isExternal} size={isMobile ? 'xs' : 'md'} className='btn btn--primary' rightIcon='ArrowRight'>
                                 {twoColumnsContent.cta.label}
                              </Button>
                           </div>
                        </div>
                     </div>
                  }
               </div>
            </div>
         }
      </div>
   )
}
